<template lang="pug">
.review-page-setup
  v-row(no-gutters)
    v-col.mr-4
      .title.mb-8
        .text-md.font-medium {{ $t('review_page_setup') }}
        .text-sm.neutral700--text.font-normal {{ $t('review_page_setup_description') }}
      .form
        v-form(ref="form")
          v-row(no-gutters)
            //- Company Logo
            v-col.mb-8(cols="12")
              .text-md.font-medium {{ $t('company_logo') }}
              .text-sm.neutral500--text.font-normal {{ $t('company_logo_description') }}
              .d-flex.align-center.mt-2
                v-card(v-if="resizeImageFileUrl" flat)
                  div(align="center")
                    v-img(:src="resizeImageFileUrl" contain max-height="64" max-width="64")
                      template(v-slot:placeholder)
                        v-row.fill-height.ma-0(align="center" justify="center")
                          v-progress-circular(indeterminate color="grey")
                v-avatar(v-else size="64" color="neutral300")
                  span.text-md.font-semibold.neutral500--text {{ getInitial(company.name) }}

                .file-change-layout.ml-4
                  v-btn.button-sm(color="basic700" outlined depressed) {{ $t('update') }}
                  input.input-change-layout(type="file" @change="onFileChange" accept="image/*" style="width: 92px !important")
            //- Heading
            v-col.mb-8(cols="8")
              .text-md.font-medium {{ $t('review_page_heading') }}
              v-text-field.text-md.font-normal.my-2.round-xl(v-model='heading' :placeholder="$t('review_page_heading_placeholder')" outlined hide-details="auto" :hint="$t('review_page_heading_hint')" persistent-hint)
            //- Description
            v-col.mb-8(cols="12")
              .text-md.font-medium {{ $t('review_page_description') }}
              v-textarea.text-md.font-normal.my-2.round-xl(v-model="description" :placeholder="$t('review_page_description_placeholder')" :rules="rules.below_250" outlined hide-details="auto" counter="250")
            //- Rating scale
            v-col.mb-8(cols="12")
              .text-md.font-medium {{ $t('review_page_rating_scale') }}
              .text-xs.neutral500--text.font-normal {{ $t('review_page_rating_scale_description') }}
              v-row.mt-4(no-gutters)
                v-col.mr-4(cols="2")
                  .text-md.font-medium {{ $t('review_page_rating_scale_scale') }}
                v-col.pr-4(cols="5")
                  .text-md.font-medium {{ $t('review_page_rating_scale_scale_description') }}
                v-col
                  .text-md.font-medium {{ $t('review_page_rating_scale_scale_send_to') }}
              .my-1(v-for="(star, starIndex) in totalStar" :key="`star-${starIndex}`")
                v-row(no-gutters align="center")
                  v-col.mr-4(cols="2")
                    star-generator(:totalStar="totalStar" :star="totalStar - starIndex" :size="25")
                  v-col.pr-4(cols="5")
                    v-text-field.text-md.font-normal.my-2.round-xl(v-model="starDescriptions[totalStar - starIndex - 1]" :placeholder="$t('review_page_heading_placeholder')" :rules="rules.not_empty" outlined hide-details="auto")
                  v-col
                    v-select.text-md.font-normal.my-2.round-xl(v-model="redirections[totalStar - starIndex - 1]" :items="redirectionOptions" outlined hide-details append-icon="mdi-chevron-down")
    //- Preview
    v-col(v-if="!isMobileScreen" cols="5")
      .sticky.mb-4
        review-page-setup-preview(:resizeImageFileUrl="resizeImageFileUrl" :heading="heading" :description="description" :totalStar="totalStar" :starDescriptions="starDescriptions")
  //- Save
  .d-flex
    v-spacer
    v-btn.button-md(color="info500" dark depressed :loading="isLoading" @click="saveData") {{ $t('save') }}
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { resizeImage } from '@/utils'
import ReviewPageSetupPreview from '@/components/review/settings/ReviewPageSetupPreview.vue'
import StarGenerator from '@/components/review/general/StarGenerator.vue'

export default {
  name: 'ReviewPageSetup',
  components: {
    ReviewPageSetupPreview,
    StarGenerator
  },
  data() {
    return {
      isLoading: false,
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        below_250: [val => (val || '').length < 250 || 'Must be below than 250 characters'],
      },
      redirectionOptions: [
        { text: 'Google review', value: 'google_review' },
        { text: 'Feedback form', value: 'feedback_form' }
      ],
      totalStar: 5,
      resizeImageFileUrl: '',
      resizeImageFile: null,
      heading: null,
      description: null,
      starDescriptions: [],
      redirections: [],
    }
  },
  props: {
    fetchingData: {
      type: Object,
      default: null,
    },
  },
  mounted() {
    this.mapData()
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    isMobileScreen() {
      return this.$vuetify.breakpoint.xs
    },
  },
  methods: {
    ...mapActions(['showMessage']),
    async onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      const resizedImage = await resizeImage(files[0], 'company_image.jpeg')
      this.resizeImageFileUrl = resizedImage.dataURL
      this.resizeImageFile = resizedImage.file
    },
    saveData() {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      const params = new FormData()
      if (this.resizeImageFile) {
        params.append('company_logo', this.resizeImageFile)
      }
      params.append('heading', this.heading)
      params.append('description', this.description)
      for (let index = 0; index < this.starDescriptions.length; index++) {
        params.append(`star_${index + 1}_description`, this.starDescriptions[index])
        params.append(`star_${index + 1}_redirection`, this.redirections[index])
      }
      this.$axios
        .patch(`/reviews/${this.company.slug}`, params)
        .then(({ data }) => {
          this.$store.commit('review/setSettings', data)
          this.showMessage(['success', this.$t('success_message.successfully_updated')])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    getInitial(value) {
      let name = value
      let rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu')
      let initials = [...name.matchAll(rgx)] || []
      initials = ((initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')).toUpperCase()
      return initials
    },
    mapData() {
      // Map description and send to data
      for (let index = 1; index <= this.totalStar; index++) {
        const descriptionName = `star_${index}_description`
        const redirectionName = `star_${index}_redirection`
        this.starDescriptions.push(this.fetchingData[descriptionName])
        this.redirections.push(this.fetchingData[redirectionName])
      }
      this.resizeImageFileUrl = this.fetchingData.company_logo?.file
      this.heading = this.fetchingData.heading
      this.description = this.fetchingData.description
    },
  },
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.no-padding-x {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.selected-button {
  background: #eeeeee !important;
  border-radius: 16px;
}
::v-deep svg.v-icon.notranslate.v-data-table-header__icon {
  height: 8px !important;
  width: 8px !important;
  margin-left: 8px;
}
.file-change-layout {
  position: relative;
}
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
.pre-wrap {
  white-space: pre-wrap;
}
.sticky {
  top: 1vh;
  position: sticky;
}
</style>